import React, { useEffect, useState, useRef, useCallback } from 'react';
import { cn } from '../../utils/cn';

export interface CountUpProps {
    /**
     * The target number to count up to
     */
    value: number;
    /**
     * The starting number (base value)
     */
    startValue?: number;
    /**
     * Duration of the animation in milliseconds
     */
    duration?: number;
    /**
     * Delay before starting the animation in milliseconds
     */
    delay?: number;
    /**
     * Whether to start the animation when the component comes into view
     */
    startOnView?: boolean;
    /**
     * Custom formatter function for the number display
     */
    formatter?: (value: number) => string;
    /**
     * CSS classes for the container
     */
    className?: string;
    /**
     * CSS classes for the number element
     */
    numberClassName?: string;
    /**
     * Callback fired when animation completes
     */
    onComplete?: () => void;
    /**
     * Callback fired when animation starts
     */
    onStart?: () => void;
}

/**
 * A component that animates a number from a start value to a target value
 * with a smooth count-up effect.
 */
export const CountUp: React.FC<CountUpProps> = ({
    value,
    startValue = 0,
    duration = 2000,
    delay = 0,
    startOnView = false,
    formatter = (val) => val.toLocaleString(),
    className,
    numberClassName,
    onComplete,
    onStart,
}) => {
    const [currentValue, setCurrentValue] = useState(startValue);
    const [isAnimating, setIsAnimating] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const elementRef = useRef<HTMLSpanElement>(null);
    const animationRef = useRef<number>();
    const startTimeRef = useRef<number>();

    const startAnimation = useCallback(() => {
        if (hasStarted) return;

        setHasStarted(true);
        setIsAnimating(true);
        onStart?.();

        const animate = (timestamp: number) => {
            if (!startTimeRef.current) {
                startTimeRef.current = timestamp;
            }

            const elapsed = timestamp - startTimeRef.current;
            const progress = Math.min(elapsed / duration, 1);

            // Easing function that slows down significantly at the end (ease-out)
            const easeOutQuint = 1 - Math.pow(1 - progress, 5);
            const newValue = startValue + (value - startValue) * easeOutQuint;

            setCurrentValue(newValue);

            if (progress < 1) {
                animationRef.current = requestAnimationFrame(animate);
            } else {
                setCurrentValue(value);
                setIsAnimating(false);
                onComplete?.();
            }
        };

        // Start animation after delay
        setTimeout(() => {
            animationRef.current = requestAnimationFrame(animate);
        }, delay);
    }, [hasStarted, onStart, duration, startValue, value, onComplete, delay]);

    // Intersection Observer for startOnView functionality
    useEffect(() => {
        if (!startOnView || !elementRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !hasStarted) {
                        startAnimation();
                    }
                });
            },
            {
                threshold: 0.1,
                rootMargin: '0px 0px 100px 0px',
            }
        );

        observer.observe(elementRef.current);

        return () => {
            observer.disconnect();
        };
    }, [startOnView, hasStarted, startAnimation]);

    // Start animation immediately if startOnView is false
    useEffect(() => {
        if (!startOnView && !hasStarted) {
            startAnimation();
        }
    }, [startOnView, hasStarted, startAnimation]);

    // Cleanup animation on unmount
    useEffect(() => {
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, []);

    // Handle value updates - animate from current value to new value
    useEffect(() => {
        if (hasStarted && !isAnimating && value !== currentValue) {
            // Start new animation from current value to new value
            setHasStarted(false);
            startTimeRef.current = undefined;
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            // Trigger new animation with current value as start
            setTimeout(() => {
                const animate = (timestamp: number) => {
                    if (!startTimeRef.current) {
                        startTimeRef.current = timestamp;
                    }

                    const elapsed = timestamp - startTimeRef.current;
                    const progress = Math.min(elapsed / duration, 1);

                    // Easing function that slows down significantly at the end (ease-out)
                    const easeOutQuint = 1 - Math.pow(1 - progress, 5);
                    const newValue =
                        currentValue + (value - currentValue) * easeOutQuint;

                    setCurrentValue(newValue);

                    if (progress < 1) {
                        animationRef.current = requestAnimationFrame(animate);
                    } else {
                        setCurrentValue(value);
                        setIsAnimating(false);
                        onComplete?.();
                    }
                };

                setHasStarted(true);
                setIsAnimating(true);
                onStart?.();

                setTimeout(() => {
                    animationRef.current = requestAnimationFrame(animate);
                }, delay);
            }, 50);
        }
    }, [
        value,
        hasStarted,
        isAnimating,
        currentValue,
        duration,
        delay,
        onComplete,
        onStart,
    ]);

    return (
        <span
            ref={elementRef}
            className={cn('inline-block', className)}
            aria-live="polite"
            aria-label={`Count: ${formatter(currentValue)}`}
        >
            <span className={numberClassName}>
                {formatter(Math.round(currentValue))}
            </span>
        </span>
    );
};
